import * as AdrDomain from "@/domain/adr"

import type { AdrIncident } from "@/domain/adr"
import type { RemediateAndHarden } from "./NextStepsTabAdr.types"

export const getThreatPrefix = (incident: AdrIncident): string => {
  const defaultPrefix = "triage.adr.tabs"

  if (AdrDomain.isVM(incident)) return defaultPrefix

  if (AdrDomain.isAiIncidentType(incident.type))
    return `triage.adr.incidents.${incident.type}`

  return defaultPrefix
}

export const getRemediateAndHarden = (
  incident: AdrIncident
): RemediateAndHarden => {
  switch (incident.type) {
    case "OS_COMMAND_INJECTION":
      return {
        steps: [
          "incidents.OS_COMMAND_INJECTION.nextSteps.remediate.steps.1",
          "tabs.nextSteps.remediate.steps.2"
        ]
      }
    case "CODE_INJECTION": {
      return {
        steps: [
          "incidents.CODE_INJECTION.nextSteps.remediate.steps.1",
          "tabs.nextSteps.remediate.steps.2"
        ]
      }
    }
    case "UNSAFE_DESERIALIZATION": {
      return {
        steps: [
          "incidents.UNSAFE_DESERIALIZATION.nextSteps.remediate.steps.1",
          "tabs.nextSteps.remediate.steps.2"
        ]
      }
    }
    case "DIRECTORY_TRAVERSAL": {
      return {
        description: "incidents.DIRECTORY_TRAVERSAL.nextSteps.remediate",
        stepsPrefix: "incidents.DIRECTORY_TRAVERSAL.nextSteps.remediate.steps",
        steps: ["1", "2", "3"]
      }
    }

    case "ANOMALOUS_SENSITIVE_FILE_READ":
      return {
        description: "tabs.nextSteps.remediate1",

        steps: [
          "incidents.ANOMALOUS_SENSITIVE_FILE_READ.nextSteps.remediate.steps.1",
          "tabs.nextSteps.remediate.steps.2"
        ]
      }

    case "CREDENTIAL_HARVESTING_SEARCH":
      return {
        description: "tabs.nextSteps.remediate1",
        stepsPrefix:
          "incidents.CREDENTIAL_HARVESTING_SEARCH.nextSteps.remediate.steps",
        steps: ["1", "2", "3"]
      }

    case "UNEXPECTED_SHELL_SPAWNED":
      return {
        description: "tabs.nextSteps.remediate1",
        stepsPrefix:
          "incidents.UNEXPECTED_SHELL_SPAWNED.nextSteps.remediate.steps",
        steps: ["1", "2"]
      }

    case "INTERACTIVE_SESSION_SYSTEM_ACCOUNT":
      return {
        description: "tabs.nextSteps.remediate1",
        stepsPrefix:
          "incidents.INTERACTIVE_SESSION_SYSTEM_ACCOUNT.nextSteps.remediate.steps",
        steps: ["1", "2"]
      }

    case "INTERACTIVE_SHELL_IN_CONTAINER":
      return {
        description: "tabs.nextSteps.remediate1",
        stepsPrefix:
          "incidents.INTERACTIVE_SHELL_IN_CONTAINER.nextSteps.remediate.steps",
        steps: ["1", "2", "3"]
      }

    case "FILELESS_EXECUTION":
      return {
        description: "tabs.nextSteps.remediate1",
        stepsPrefix: "incidents.FILELESS_EXECUTION.nextSteps.remediate.steps",
        steps: ["1", "2"]
      }

    case "PTRACE_ANTI_DEBUG":
      return {
        description: "tabs.nextSteps.remediate1",
        stepsPrefix: "incidents.PTRACE_ANTI_DEBUG.nextSteps.remediate.steps",
        steps: ["1", "2", "3"]
      }

    case "PTRACE_ATTACH":
      return {
        description: "tabs.nextSteps.remediate1",
        stepsPrefix: "incidents.PTRACE_ATTACH.nextSteps.remediate.steps",
        steps: ["1", "2", "3"]
      }

    case "EXPOSURE_TO_NEWLY_PUBLISHED_CVE":
      return {
        description:
          "incidents.EXPOSURE_TO_NEWLY_PUBLISHED_CVE.nextSteps.remediate",
        stepsPrefix:
          "incidents.EXPOSURE_TO_NEWLY_PUBLISHED_CVE.nextSteps.remediate.steps",
        steps: ["1", "2", "3"]
      }

    case "POTENTIAL_EXPLOIT_CVE": {
      return {
        steps: [
          "incidents.POTENTIAL_EXPLOIT_CVE.nextSteps.remediate.steps.1",
          "tabs.nextSteps.remediate.steps.2"
        ]
      }
    }

    case "PROMPT_INJECTION_WITH_SENSITIVE_OUTPUT":
      return {
        stepsPrefix:
          "incidents.PROMPT_INJECTION_WITH_SENSITIVE_OUTPUT.nextSteps.remediate.steps",
        steps: ["1", "2", "3"]
      }

    case "LLM_MISUSE_TRIGGERED_UNEXPECTED_SHELL_ATTEMPT":
      return {
        stepsPrefix:
          "incidents.LLM_MISUSE_TRIGGERED_UNEXPECTED_SHELL_ATTEMPT.nextSteps.remediate.steps",
        steps: ["1", "2", "3"]
      }

    case "POISONED_LOG_LINE_TRIGGERED_UNAPPROVED_PRODUCTION_SHELL":
      return {
        stepsPrefix:
          "incidents.POISONED_LOG_LINE_TRIGGERED_UNAPPROVED_PRODUCTION_SHELL.nextSteps.remediate.steps",
        steps: ["1", "2", "3"]
      }

    case "UNSANITIZED_PROMPT_TRIGGERED_AI_AGENT_CODE_EXECUTION":
      return {
        stepsPrefix:
          "incidents.UNSANITIZED_PROMPT_TRIGGERED_AI_AGENT_CODE_EXECUTION.nextSteps.remediate.steps",
        steps: ["1", "2", "3"]
      }

    case "UNSANCTIONED_NETWORK_ACCESS":
      return {
        stepsPrefix:
          "incidents.UNSANCTIONED_NETWORK_ACCESS.nextSteps.remediate.steps",
        steps: ["1", "2", "3"]
      }

    case "CODE_TAMPERING_LIBRARY_LOAD": {
      return {
        description: "tabs.nextSteps.remediate2",
        stepsPrefix: "tabs.nextSteps.remediate2.steps",
        steps: ["1", "2", "3"]
      }
    }

    case "CODE_TAMPERING_PROCESS_EXECUTION": {
      if (AdrDomain.isVM(incident))
        return {
          description: "tabs.nextSteps.remediate",
          stepsPrefix:
            "incidents.CODE_TAMPERING_PROCESS_EXECUTION.nextSteps.remediate.steps",
          steps: ["1", "2", "3", "4"]
        }

      return {
        description: "tabs.nextSteps.remediate2",
        stepsPrefix: "tabs.nextSteps.remediate2.steps",
        steps: ["1", "2", "3", "4"]
      }
    }

    case "CODE_TAMPERING_SCRIPT_LOAD": {
      return {
        stepsPrefix:
          "incidents.CODE_TAMPERING_SCRIPT_LOAD.nextSteps.remediate.steps",
        steps: ["1", "2", "3", "4"]
      }
    }

    case "EXEC_MEMORY_NON_JIT": {
      return {
        stepsPrefix: "incidents.EXEC_MEMORY_NON_JIT.nextSteps.remediate.steps",
        steps: ["1", "2", "3", "4"]
      }
    }

    case "EXEC_MEMORY_ANOMALOUS_JIT": {
      return {
        stepsPrefix:
          "incidents.EXEC_MEMORY_ANOMALOUS_JIT.nextSteps.remediate.steps",
        steps: ["1", "2", "3", "4", "5"]
      }
    }

    default:
      return {
        stepsPrefix: "tabs.nextSteps.remediate.steps",
        steps: ["1", "2"]
      }
  }
}
